import type { BusinessUnit, BusinessUnitDraft, BusinessUnitUpdate, MessageToNotify } from 'services/types/businessUnit';

import baseApi from './baseApi';

// Type definition for OpenAI function tools
export interface ChatCompletionTool {
  type: 'function';
  function: {
    name: string;
    description: string;
    parameters: {
      type: string;
      properties: Record<string, unknown>;
      required?: string[];
    };
  };
}

const adminBusinessUnitsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAdminBusinessUnits: build.query({
      query: () => ({
        url: '/admin/businessUnits',
        method: 'GET',
      }),
      transformResponse: (response: BusinessUnit[]) => response,
    }),
    getAdminBusinessUnit: build.query({
      query: (id: number) => ({
        url: `/admin/businessUnits/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: BusinessUnit) => response,
    }),
    editAdminBusinessUnit: build.mutation({
      query: (props: { businessUnit: BusinessUnitUpdate; id: number }) => ({
        url: `admin/businessUnits/${props.id}`,
        method: 'PATCH',
        body: props.businessUnit,
      }),
      transformResponse: (response: BusinessUnit) => response,
    }),
    createAdminBusinessUnit: build.mutation({
      query: (company: BusinessUnitDraft) => ({
        url: `admin/businessUnits`,
        method: 'POST',
        body: company,
      }),
      transformResponse: (response: BusinessUnit) => response,
    }),
    deleteAdminBusinessUnitImage: build.mutation({
      query: (props: { businessUnitId: number; fileName: string }) => ({
        url: `/admin/businessUnits/${props.businessUnitId}/images/${props.fileName}`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) => response,
    }),
    uploadAdminBusinessUnitImages: build.mutation({
      query: (props: { businessUnitId: number; files: File[] }) => {
        const formData = new FormData();
        props.files.forEach((file) => {
          formData.append('file', file);
        });
        return {
          url: `/admin/businessUnits/${props.businessUnitId}/images`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: unknown) => response,
    }),
    deleteAdminBusinessUnitVideo: build.mutation({
      query: (props: { businessUnitId: number; fileName: string }) => ({
        url: `/admin/businessUnits/${props.businessUnitId}/videos/${props.fileName}`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) => response,
    }),
    uploadAdminBusinessUnitVideos: build.mutation({
      query: (props: { businessUnitId: number; files: File[] }) => {
        const formData = new FormData();
        props.files.forEach((file) => {
          formData.append('file', file);
        });
        return {
          url: `/admin/businessUnits/${props.businessUnitId}/videos`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: unknown) => response,
    }),
    deleteAdminBusinessUnitDocument: build.mutation({
      query: (props: { businessUnitId: number; fileName: string }) => ({
        url: `/admin/businessUnits/${props.businessUnitId}/documents/${props.fileName}`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) => response,
    }),
    uploadAdminBusinessUnitDocuments: build.mutation({
      query: (props: { businessUnitId: number; files: File[] }) => {
        const formData = new FormData();
        props.files.forEach((file) => {
          formData.append('file', file);
        });
        return {
          url: `/admin/businessUnits/${props.businessUnitId}/documents`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: unknown) => response,
    }),
    editAdminBusinessUnitMessagesToNotify: build.mutation({
      query: (props: { messagesToNotify: MessageToNotify[]; id: number }) => ({
        url: `admin/businessUnits/messages-to-notify/${props.id}`,
        method: 'PATCH',
        body: props.messagesToNotify,
      }),
      transformResponse: (response: BusinessUnit) => response,
    }),
    getAdminAvailableFunctions: build.query({
      query: () => ({
        url: '/admin/businessUnits/available-functions',
        method: 'GET',
      }),
      transformResponse: (response: { availableFunctions: ChatCompletionTool[] }): ChatCompletionTool[] =>
        response.availableFunctions,
    }),
    configureBusinessUnitSmtpEmail: build.mutation({
      query: (props: {
        businessUnitId: number;
        address: string;
        smtpHost: string;
        smtpPort: number;
        imapHost: string;
        imapPort: number;
        password?: string;
        draftMode?: boolean;
      }) => {
        const { businessUnitId, ...body } = props;
        return {
          url: `/admin/businessUnits/${businessUnitId}/email-smtp`,
          method: 'POST',
          body,
        };
      },
      transformResponse: (response: unknown) => response,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAdminBusinessUnitsQuery,
  useGetAdminBusinessUnitQuery,
  useEditAdminBusinessUnitMutation,
  useCreateAdminBusinessUnitMutation,
  useDeleteAdminBusinessUnitImageMutation,
  useUploadAdminBusinessUnitImagesMutation,
  useDeleteAdminBusinessUnitVideoMutation,
  useUploadAdminBusinessUnitVideosMutation,
  useDeleteAdminBusinessUnitDocumentMutation,
  useUploadAdminBusinessUnitDocumentsMutation,
  useEditAdminBusinessUnitMessagesToNotifyMutation,
  useGetAdminAvailableFunctionsQuery,
  useConfigureBusinessUnitSmtpEmailMutation,
} = adminBusinessUnitsApi;
